import React, { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { getArSyncAging, getArSyncCollections, getArSyncCollectionPeriods, getProjects, getArNotes, addArNote } from '../api';
import LoadingState from '../components/LoadingState';
import StateScreen from '../components/ui/StateScreen';
import SegmentedControl from '../components/ui/SegmentedControl';
import KpiRow from '../components/ar/ArKpiRow';
import { useSearchParams } from 'react-router-dom';
import { usePageSearchState, matchesSearch, noMatchText } from '../lib/pageSearch';
import { num, daysBetween, outstandingOf, arOutTotals, arAgingBuckets, avgDaysToPay } from '../lib/arTotals';
import InvoiceHistoryPage from './InvoiceHistoryPage';
import StatementsView from '../components/ar/StatementsView';
import BillingByPhaseView from '../components/ar/BillingByPhaseView';
import { todayCentral } from '../lib/invoiceHistory';

/* ═══════════════════════════════════════════════════════════════════════════
   Accounts Receivable (Collections) page — CE mirror edition
   Top-level role-gated route (/ar). Reads the CE ACTUALS mirrored by the
   CE-Agent (see /api/ar-sync): every open invoice with its real balance and
   true days-past-due (ar_aging_detail, as-of the latest snapshot), and every
   payment CE received per month (ar_collection_detail). Joined to Summit
   projects by CE job number — projects not in Summit's billing flow (the old
   half-imported monitoring jobs) show straight from CE.
   Sign convention (pinned 2026-07-13): CE reports collections as NEGATIVE
   amounts (payments credit AR), so cash collected = −SUM(amount) on
   job-bearing rows. ON ACCT rows are unapplied-cash legs with no job.
   ═══════════════════════════════════════════════════════════════════════════ */

/* full figure, cents — tiles, table, totals, legend */
const fmt$ = (n) => {
  if (n == null || Number.isNaN(+n)) return '—';
  return (+n).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
/* compact $X.XM / $Xk — ONLY the inline aging-bar segment labels */
const fmt$c = (n) => {
  if (n == null || Number.isNaN(+n)) return '—';
  const a = Math.abs(+n);
  if (a >= 1e6) {
    const m = a / 1e6;
    return '$' + (m >= 100 ? m.toFixed(0) : m.toFixed(1)) + 'M';
  }
  if (a >= 1e3) return '$' + Math.round(a / 1e3) + 'k';
  return '$' + Math.round(a);
};
/* DATE columns arrive as ISO-midnight-UTC strings — anchor to the date part
   so a due date never shifts a day in local time. */
const fmtDate = (d) => {
  if (!d) return '—';
  const s = String(d);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  const dt = new Date(m ? m[1] + 'T00:00:00' : s);
  if (Number.isNaN(dt.getTime())) return s;
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const fmtPeriod = (ym) => {
  if (!ym) return '—';
  const [y, m] = String(ym).split('-').map(Number);
  const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${MONTHS[m] || '?'} ${y}`;
};
// The Outstanding rules (collectible rows, totals, aging tiers, days to pay) are shared with the
// Accounting dashboard's Money to collect (lib/arTotals.js), so the two always agree.

/* aging ramp — TRUE days past DUE, 3 tiers matching the accounting team's
   Open AR workbook legend: <30 green · 30–60 yellow · >60 red.
   Negative = not yet due (shown as the raw number, like the workbook). */
const agingTone = (days) => {
  if (days == null || days < 30) return 'var(--success)';
  if (days <= 60) return 'var(--warning)';
  return 'var(--danger)';
};
const agingSoft = (days) => `color-mix(in oklch, ${agingTone(days)} 14%, var(--bg-surface))`;
const agingLabel = (days) => (days == null ? '—' : `${days}d`);
const ring = (c) => `color-mix(in oklch, ${c} 22%, transparent)`;

/* ─────────────── Inline SVG icons ─────────────── */

const Icon = ({ name, size = 16, sw = 1.6 }) => {
  const s = size;
  const p = (d) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
      {d}
    </svg>
  );
  switch (name) {
    case 'x':
      return p(<path d="M6 6l12 12M6 18 18 6" />);
    case 'chevron-d':
      return p(<path d="m6 9 6 6 6-6" />);
    case 'sort':
      return p(
        <>
          <path d="M8 9l4-4 4 4" />
          <path d="M8 15l4 4 4-4" />
        </>
      );
    case 'caret-up':
      return p(<path d="M7 14l5-5 5 5" />);
    case 'caret-down':
      return p(<path d="M7 10l5 5 5-5" />);
    case 'external':
      return p(
        <>
          <path d="M14 4h6v6" />
          <path d="M20 4l-9 9" />
          <path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />
        </>
      );
    case 'plus':
      return p(<path d="M12 5v14M5 12h14" />);
    case 'msg':
      return p(<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />);
    default:
      return null;
  }
};

/* ─────────────── Aging summary card (functional colors — true past-due buckets) ─────────────── */

const AgingCard = ({ buckets }) => {
  const [hover, setHover] = useState(null);
  const total = buckets.reduce((sum, b) => sum + b.amount, 0);
  const shown = buckets.filter((b) => b.amount > 0);

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '16px 18px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Aging — days past due</div>

      <div style={{ overflowX: 'auto', overflowY: 'visible' }}>
        <div style={{ minWidth: 420, position: 'relative', paddingTop: total > 0 ? 22 : 0 }}>
          {/* Above-bar $ callouts for segments too narrow to hold the label inside. */}
          {total > 0 && shown.map((b, i) => {
            const share = (b.amount / total) * 100;
            if (share >= 12) return null;
            const center = shown.slice(0, i).reduce((s, x) => s + (x.amount / total) * 100, 0) + share / 2;
            return (
              <span key={`lbl-${b.key}`} className="mono" style={{ position: 'absolute', top: 0, left: `${center}%`, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: 12, fontWeight: 700, color: b.tone, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>{fmt$c(b.amount)}</span>
            );
          })}
          <div style={{ display: 'flex', height: 28, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)' }}>
            {total === 0 ? (
              <div style={{ flex: 1 }} />
            ) : (
              shown.map((b, i) => {
                const share = (b.amount / total) * 100;
                const wide = share >= 12;
                return (
                  <div
                    key={b.key}
                    onMouseEnter={() => setHover(b.key)}
                    onMouseLeave={() => setHover(null)}
                    style={{ width: `${share}%`, background: b.tone, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRight: i < shown.length - 1 ? '1px solid color-mix(in oklch, var(--bg-surface) 35%, transparent)' : 'none', cursor: 'default', minWidth: 2 }}
                  >
                    {wide && (
                      <span className="mono" style={{ fontSize: 13, fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', whiteSpace: 'nowrap', padding: '0 6px', textShadow: '0 1px 1px oklch(0 0 0 / 0.18)' }}>
                        {fmt$c(b.amount)}
                      </span>
                    )}
                    {hover === b.key && (
                      <div style={{ position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)', zIndex: 20, whiteSpace: 'nowrap', pointerEvents: 'none', background: 'var(--text-1)', color: 'var(--bg-surface)', borderRadius: 7, padding: '7px 10px', boxShadow: 'var(--shadow-md)' }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, letterSpacing: '0.01em', marginBottom: 2, opacity: 0.85 }}>{b.label} · {b.count} {b.count === 1 ? 'invoice' : 'invoices'}</div>
                        <div className="mono" style={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt$(b.amount)}</div>
                        <span style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid var(--text-1)' }} />
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 26px' }}>
        {buckets.map((b) => {
          const zero = b.amount === 0;
          return (
            <div key={b.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, flex: '0 0 auto', background: b.tone, opacity: zero ? 0.4 : 1 }} />
              <span style={{ fontSize: 14, color: 'var(--text-3)', fontWeight: 500 }}>{b.label}:</span>
              <span className="mono" style={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', color: zero ? 'var(--text-4)' : 'var(--text-1)' }}>{fmt$(b.amount)}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
};

/* ─────────────── Filter bar ─────────────── */

const Select = ({ value, onChange, children, minWidth = 200 }) => (
  <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="a-select"
      style={{ appearance: 'none', WebkitAppearance: 'none', height: 32, padding: '0 32px 0 12px', borderRadius: 8, minWidth, maxWidth: 320, fontSize: 14, fontWeight: 500, color: 'var(--text-1)', fontFamily: 'var(--font-sans)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer', letterSpacing: '-0.005em', textOverflow: 'ellipsis' }}
    >
      {children}
    </select>
    <span style={{ position: 'absolute', right: 10, color: 'var(--text-3)', pointerEvents: 'none', display: 'inline-flex' }}>
      <Icon name="chevron-d" size={14} />
    </span>
  </div>
);

/* ─────────────── Table primitives ─────────────── */

const SortGlyph = ({ active, dir }) => (
  <span className="a-sorticon" style={{ display: 'inline-flex', flex: '0 0 auto', color: active ? 'var(--accent)' : 'var(--text-4)', opacity: active ? 1 : 0 }}>
    <Icon name={active ? (dir === 'asc' ? 'caret-up' : 'caret-down') : 'sort'} size={13} sw={2} />
  </span>
);

const SortTh = ({ id, label, align = 'left', w, sortKey, sortDir, onSort }) => {
  const active = sortKey === id;
  return (
    <th style={{ position: 'sticky', top: 'var(--ar-th-top, 0px)', zIndex: 2, background: 'var(--bg-surface-2)', textAlign: align, width: w, padding: 0, borderBottom: '1px solid var(--border-default)', whiteSpace: 'normal' }}>
      <button
        type="button"
        className="a-th"
        onClick={() => onSort(id)}
        /* Tighter tracking/size to narrow the labels; the single-lining itself is a
           GATED css rule (see .a-th in the style block), not an inline whiteSpace.
           Tracking is the expensive part on uppercase text, so 0.05em -> 0.02em buys
           most of the width; 12 -> 11.5px covers the rest. */
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, width: '100%', justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start', padding: '10px 14px', cursor: 'pointer', background: 'transparent', border: 'none', fontSize: 11.5, fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase', color: active ? 'var(--accent-text)' : 'var(--text-3)', fontFamily: 'var(--font-sans)' }}
      >
        {align === 'right' && <SortGlyph active={active} dir={sortDir} />}
        <span>{label}</span>
        {align !== 'right' && <SortGlyph active={active} dir={sortDir} />}
      </button>
    </th>
  );
};

const AgePill = ({ days }) => {
  if (days == null) return <span className="mono" style={{ fontSize: 13.5, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>—</span>;
  const tone = agingTone(days);
  return (
    <span className="mono" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 52, height: 22, padding: '0 9px', borderRadius: 999, fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em', color: tone, background: agingSoft(days), border: `1px solid ${ring(tone)}`, whiteSpace: 'nowrap' }}>
      {agingLabel(days)}
    </span>
  );
};

const Money = ({ value, color, bold }) => (
  <td className="mono" style={{ padding: '11px 14px', textAlign: 'right', fontSize: 14, fontWeight: bold ? 700 : 500, color: color || 'var(--text-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
    {fmt$(value)}
  </td>
);

const FootMoney = ({ value, color, bold }) => (
  <td className="mono" style={{ position: 'sticky', bottom: 0, zIndex: 2, background: 'var(--bg-surface-2)', padding: '12px 14px', textAlign: 'right', borderTop: '1px solid var(--border-default)', fontSize: 14, fontWeight: bold ? 800 : 600, color: color || 'var(--text-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
    {fmt$(value)}
  </td>
);

const ProjectCell = ({ row, onOpenProject }) => {
  const clickable = onOpenProject && row.project_ref;
  return (
    <td style={{ padding: '10px 14px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
        <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240 }}>{row.display_name}</span>
        {row.display_number && (
          clickable ? (
            <button type="button" className="a-projlink" onClick={(e) => { e.stopPropagation(); onOpenProject(row.project_ref); }} title="Open project" style={{ display: 'inline-flex', alignSelf: 'flex-start', alignItems: 'center', gap: 4, padding: 0, border: 'none', background: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 500, color: 'var(--text-3)' }}>
              <span className="mono" style={{ fontVariantNumeric: 'tabular-nums' }}>#{row.display_number}</span>
              <span style={{ display: 'inline-flex', color: 'var(--text-4)' }}><Icon name="external" size={12} sw={1.7} /></span>
            </button>
          ) : (
            <span className="mono" style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>#{row.display_number}</span>
          )
        )}
      </div>
    </td>
  );
};

/* ─────────────── sort helpers ─────────────── */

const dateVal = (d) => {
  if (!d) return 0;
  const t = new Date(d).getTime();
  return Number.isNaN(t) ? 0 : t;
};

const OUT_SORT = {
  project: (r) => r.display_name || '',
  bd: (r) => r.business_development || '',
  pm: (r) => r.project_manager || '',
  invoice: (r) => r.invoice_number || '',
  invdate: (r) => dateVal(r.invoice_date),
  duedate: (r) => dateVal(r.due_date),
  age: (r) => (r.days_past_due == null ? -10000 : r.days_past_due),
  balance: (r) => num(r.balance),
};

const CASH_SORT = {
  project: (r) => r.display_name || '',
  invoice: (r) => r.invoice_number || '',
  invdate: (r) => dateVal(r.invoice_date),
  paydate: (r) => dateVal(r.payment_date),
  daystopay: (r) => (r.days_to_pay == null ? -10000 : r.days_to_pay),
  amount: (r) => r.collected,
};

function sortRows(rows, fields, sortKey, sortDir) {
  const f = fields[sortKey];
  if (!f) return rows;
  const sorted = [...rows].sort((a, b) => {
    const va = f(a);
    const vb = f(b);
    const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
    return sortDir === 'asc' ? cmp : -cmp;
  });
  return sorted;
}

/* ═════════ Outstanding table + in-place 3D notes pop-out (NotesLift) ═════════
   Clicking a row measures its <tr>, hides it, and portals a pixel-aligned
   replica (same cell renderers, measured widths) that lifts toward the viewer
   while the note thread unfolds beneath and the page blurs behind. Ported from
   the Claude Design handoff (design_handoff_ar_notes_popout). */

const CELL_PAD = '10px 14px';
// Text cells wrap (don't truncate) so the table can shrink into a narrow container
// by getting taller instead of overflowing — wide screens stay roomy/one-line.
const cellTxt = { fontSize: 14, color: 'var(--text-2)', minWidth: 0, overflowWrap: 'anywhere', lineHeight: 1.35 };
const cellMono = { fontSize: 13.5, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

/* notes-cell inner: count chip + latest note truncated, or faint "+ note" */
const NotesPreview = ({ notes }) => notes.length ? (
  <span style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 8, minWidth: 0 }}>
    <span className="mono" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 20, padding: '0 7px', borderRadius: 999, fontSize: 11, fontWeight: 600, fontVariantNumeric: 'tabular-nums', flex: '0 0 auto', color: 'var(--text-2)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-default)' }}><Icon name="msg" size={11} sw={1.8} />{notes.length}</span>
    {/* Clamp the preview to 3 lines so a long note doesn't make the row huge when
        the column is narrow — the full note is one click away in the pop-out. */}
    <span style={{ fontSize: 12.5, color: 'var(--text-3)', minWidth: 0, overflowWrap: 'anywhere', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} title={notes[notes.length - 1].note}>{notes[notes.length - 1].note}</span>
  </span>
) : (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 500, color: 'var(--text-4)' }}><Icon name="plus" size={12} sw={2} /> note</span>
);

/* project cell inner — name text + #number link (external icon), like Job Cost */
const ProjectCellInner = ({ r, onOpenProject }) => {
  const clickable = onOpenProject && r.project_ref;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
      <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-1)', overflowWrap: 'anywhere', lineHeight: 1.25 }}>{r.display_name}</span>
      {r.display_number && (clickable ? (
        <button type="button" className="a-projlink" onClick={(e) => { e.stopPropagation(); onOpenProject(r.project_ref); }} title="Open project" style={{ display: 'inline-flex', alignSelf: 'flex-start', alignItems: 'center', gap: 4, padding: 0, border: 'none', background: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 500, color: 'var(--text-3)' }}>
          <span className="mono" style={{ fontVariantNumeric: 'tabular-nums' }}>#{r.display_number}</span>
          <span style={{ display: 'inline-flex', color: 'var(--text-4)' }}><Icon name="external" size={12} sw={1.7} /></span>
        </button>
      ) : (
        <span className="mono" style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>#{r.display_number}</span>
      ))}
    </div>
  );
};

/* Column defs — the SAME cell renderers draw the real <td>s AND the lifted
   replica, so the pop-out overlays the row pixel-for-pixel. ctx = { mode, notes,
   onOpenProject }. */
// Widths are percentages: the table is table-layout:fixed, so columns scale
// proportionally at every width (no column hogs slack) and shrink to fit narrow
// screens, with headers + text cells wrapping into taller rows.
const OUT_COLS = [
  { id: 'project', label: 'Project', align: 'left', w: '15%', sortable: true, cell: (r, ctx) => <ProjectCellInner r={r} onOpenProject={ctx.onOpenProject} /> },
  { id: 'bd', label: 'Biz Dev', align: 'left', w: '10.5%', sortable: true, cell: (r) => <span style={cellTxt}>{r.business_development || '—'}</span> },
  { id: 'pm', label: 'PM', align: 'left', w: '10.5%', sortable: true, cell: (r) => <span style={cellTxt}>{r.project_manager || '—'}</span> },
  { id: 'invoice', label: 'Invoice', align: 'left', w: '8%', sortable: true, cell: (r) => <span className="mono" style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{r.invoice_number}</span> },
  { id: 'invdate', label: 'Invoice Date', align: 'left', w: '11%', sortable: true, cell: (r) => <span className="mono" style={cellMono}>{fmtDate(r.invoice_date)}</span> },
  { id: 'duedate', label: 'Due Date', align: 'left', w: '11%', sortable: true, cell: (r) => <span className="mono" style={cellMono}>{fmtDate(r.due_date)}</span> },
  { id: 'age', label: 'Past Due', align: 'center', w: '8%', sortable: true, cell: (r) => <AgePill days={r.days_past_due} /> },
  { id: 'balance', label: 'Balance', align: 'right', w: '13%', sortable: true, cell: (r) => (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
      <span className="mono" style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>{fmt$(num(r.balance))}</span>
      {/* part-credited (lib/arTotals outstandingOf): the balance is what the credit memo leaves */}
      {r.credits && (
        <span className="mono" title={`Invoiced ${fmt$(r.invoiced)}; ${r.credits.map((c) => c.invoice_number).join(', ')} credits ${fmt$(r.credit)}`} style={{ fontSize: 11.5, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          after {r.credits.map((c) => c.invoice_number).join(', ')} {fmt$(r.credit)}
        </span>
      )}
    </span>
  ) },
  { id: 'notes', label: 'Notes', align: 'left', w: '13%', sortable: false, cell: (r, ctx) => <span className={ctx.mode === 'replica' ? 'arn-np' : undefined} style={{ display: 'inline-flex', minWidth: 0, maxWidth: '100%' }}><NotesPreview notes={ctx.notes} /></span> },
];

/* ── pop-out CSS (keyframes + pseudo-selectors can't be inline) — injected once ── */
const ARN_CSS = `
.arn-layer{position:fixed;inset:0;z-index:1000;--t:var(--arn-t,1)}
.arn-backdrop{position:absolute;inset:0;background:color-mix(in oklch, var(--bg-page) 52%, transparent);-webkit-backdrop-filter:blur(var(--arn-blur,7px)) saturate(.9);backdrop-filter:blur(var(--arn-blur,7px)) saturate(.9);opacity:0;transition:opacity calc(.45s*var(--t)) cubic-bezier(.3,.6,.3,1)}
.arn-layer.is-open .arn-backdrop{opacity:1}
.arn-layer.is-closing .arn-backdrop{opacity:0;transition-duration:calc(.32s*var(--t))}
.arn-card{position:fixed;background:var(--bg-surface);border:1px solid transparent;border-radius:2px;transform:translate3d(0,0,0) scale(1);box-shadow:0 1px 2px oklch(0 0 0/0),0 12px 24px -8px oklch(0 0 0/0),0 32px 64px -14px oklch(0 0 0/0);transition:transform calc(.55s*var(--t)) cubic-bezier(.3,1.36,.32,1),box-shadow calc(.55s*var(--t)) cubic-bezier(.3,1,.35,1),border-color calc(.3s*var(--t)) ease,border-radius calc(.45s*var(--t)) ease;will-change:transform,box-shadow}
.arn-layer.is-open .arn-card{transform:translate3d(0,var(--arn-dy,0px),0) scale(1.013);border-color:var(--border-default);border-radius:14px;box-shadow:var(--shadow-lift)}
.arn-layer.is-closing .arn-card{transition:transform calc(.36s*var(--t)) cubic-bezier(.35,.9,.3,1),box-shadow calc(.36s*var(--t)) ease,border-color calc(.25s*var(--t)) ease,border-radius calc(.3s*var(--t)) ease}
.arn-tilt{border-radius:inherit;overflow:hidden;background:inherit;transition:transform .18s ease-out}
.arn-layer.is-open .arn-tilt{animation:arnTip calc(.75s*var(--t)) cubic-bezier(.32,.75,.3,1)}
@keyframes arnTip{0%{transform:perspective(1200px) rotateX(0deg)}34%{transform:perspective(1200px) rotateX(1.1deg)}100%{transform:perspective(1200px) rotateX(0deg)}}
.arn-rowstrip{display:flex;align-items:stretch;background:var(--bg-hover);transition:background calc(.4s*var(--t)) ease}
.arn-layer.is-open .arn-rowstrip{background:transparent}
.arn-panel{display:grid;grid-template-rows:0fr;transition:grid-template-rows calc(.52s*var(--t)) cubic-bezier(.3,1,.3,1)}
.arn-layer.is-open .arn-panel{grid-template-rows:1fr}
.arn-layer.is-closing .arn-panel{transition-duration:calc(.34s*var(--t))}
.arn-panel>div{min-height:0;overflow:hidden}
.arn-panelin{opacity:0;transform:translateY(-6px);transition:opacity calc(.35s*var(--t)) ease calc(.08s*var(--t)),transform calc(.45s*var(--t)) cubic-bezier(.3,1.2,.35,1) calc(.06s*var(--t))}
.arn-layer.is-open .arn-panelin{opacity:1;transform:none}
.arn-layer.is-closing .arn-panelin{opacity:0;transform:translateY(-4px);transition:opacity calc(.18s*var(--t)) ease,transform calc(.3s*var(--t)) ease}
.arn-np{transition:opacity calc(.25s*var(--t)) ease}
.arn-layer.is-open .arn-np{opacity:0;pointer-events:none}
.arn-nx{position:absolute;transform:translateY(-50%) scale(.8);opacity:0;pointer-events:none;width:30px;height:30px;border-radius:8px;display:inline-flex;align-items:center;justify-content:center;color:var(--text-3);background:var(--bg-surface);border:1px solid var(--border-default);box-shadow:var(--shadow-xs);cursor:pointer;transition:opacity calc(.3s*var(--t)) ease calc(.1s*var(--t)),transform calc(.35s*var(--t)) cubic-bezier(.3,1.4,.35,1) calc(.1s*var(--t)),color .15s,border-color .15s,background .15s}
.arn-layer.is-open .arn-nx{opacity:1;transform:translateY(-50%) scale(1);pointer-events:auto}
.arn-nx:hover{color:var(--text-1);border-color:var(--border-strong);background:var(--bg-surface-2)}
.arn-thread{scrollbar-width:thin;scrollbar-color:var(--border-strong) transparent}
.arn-thread::-webkit-scrollbar{width:8px}
.arn-thread::-webkit-scrollbar-thumb{background:var(--border-default);border-radius:8px;border:2px solid transparent;background-clip:content-box}
.arn-fresh{animation:arnNoteIn calc(.45s*var(--t)) cubic-bezier(.3,1.2,.35,1)}
@keyframes arnNoteIn{from{opacity:0;transform:translateY(-5px)}to{opacity:1;transform:none}}
.arn-ta{width:100%;box-sizing:border-box;resize:none;min-height:60px;padding:9px 12px;border-radius:9px;font:500 13px/1.5 var(--font-sans);letter-spacing:-0.005em;color:var(--text-1);background:var(--bg-surface);border:1px solid var(--border-default);box-shadow:var(--shadow-xs);transition:border-color .15s,box-shadow .15s}
.arn-ta::placeholder{color:var(--text-4)}
.arn-ta:focus{outline:none;border-color:var(--accent);box-shadow:0 0 0 3px var(--accent-ring)}
.arn-add{display:inline-flex;align-items:center;gap:6px;height:32px;padding:0 14px;border-radius:8px;font:600 12.5px var(--font-sans);letter-spacing:-0.005em;color:#fff;background:var(--accent);border:1px solid color-mix(in oklch, var(--accent) 50%, oklch(0.3 0.05 135));box-shadow:0 1px 0 oklch(0.42 0.06 135),0 1px 2px oklch(0.4 0.05 135/0.25);cursor:pointer;transition:background .15s}
.arn-add:hover:not(:disabled){background:var(--accent-hover)}
.arn-add:disabled{opacity:.45;cursor:default}
.app.dark .arn-add,.theme-dark .arn-add{box-shadow:0 1px 0 oklch(0.55 0.07 135),inset 0 1px 0 oklch(0.88 0.05 135/0.18);color:oklch(0.15 0.01 100)}
@media (prefers-reduced-motion: reduce){
  .arn-backdrop,.arn-card,.arn-panel,.arn-panelin,.arn-np,.arn-nx,.arn-rowstrip{transition-duration:.01ms!important;transition-delay:0s!important}
  .arn-tilt,.arn-fresh{animation:none!important}
}`;
let arnInjected = false;
const injectArnCss = () => {
  if (arnInjected || typeof document === 'undefined' || document.getElementById('arn-popout-css')) { arnInjected = true; return; }
  const s = document.createElement('style'); s.id = 'arn-popout-css'; s.textContent = ARN_CSS;
  document.head.appendChild(s); arnInjected = true;
};
const motionScale = () => {
  if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0.05;
  return 1;
};

const NoteItem = ({ n, first, last, fresh }) => (
  <div className={fresh ? 'arn-fresh' : undefined} style={{ display: 'flex', gap: 12 }}>
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto', width: 8, paddingTop: 6 }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, flex: '0 0 auto', background: first ? 'var(--accent)' : 'var(--border-strong)' }} />
      {!last && <span style={{ width: 1, flex: 1, background: 'var(--border-subtle)', marginTop: 4 }} />}
    </div>
    <div style={{ flex: 1, minWidth: 0, paddingBottom: last ? 2 : 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-1)' }}>{n.user_name}</span>
        <span className="mono" style={{ fontSize: 11, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{fmtDate(n.created_at)}</span>
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text-2)', marginTop: 2, textWrap: 'pretty' }}>{n.note}</div>
    </div>
  </div>
);

/* The in-place 3D pop-out — replaces the centered modal. */
const NotesLift = ({ inv, notes, rect, cells, offX = 0, returnFocus, onAddNote, onOpenProject, onClosed }) => {
  injectArnCss();
  const [state, setState] = useState('pre'); // pre → open → closing
  const stRef = useRef(state); stRef.current = state;
  const [dy, setDy] = useState(0);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [freshId, setFreshId] = useState(null);
  const [err, setErr] = useState(null);
  // The card is portaled to <body>, OUTSIDE the app's `.app.dark` container, so it
  // doesn't inherit the dark-theme token overrides (they're scoped to `.app.dark`).
  // Detect the active theme at open time and re-apply the theme class on our layer.
  const [isDark] = useState(() => typeof document !== 'undefined' && !!document.querySelector('.app.dark, .theme-dark'));
  const cardRef = useRef(null);
  const panelRef = useRef(null);
  const threadRef = useRef(null);
  const taRef = useRef(null);

  /* measure expanded height → clamp the lift into the viewport, then spring open */
  useLayoutEffect(() => {
    const M = 16;
    const panelH = panelRef.current ? panelRef.current.offsetHeight : 0;
    const total = rect.height + panelH + 2;
    const overflow = (rect.top + total + M) - window.innerHeight;
    if (overflow > 0) setDy(-Math.round(Math.min(overflow, Math.max(rect.top - M, 0))));
    // Move focus into the dialog immediately (the row that opened it isn't the
    // focused element), so a Tab during the ~180ms before the composer autofocuses
    // stays trapped inside the card instead of escaping behind the backdrop.
    if (cardRef.current) cardRef.current.focus({ preventScroll: true });
    // Flip to 'open' next frame so the spring animates from the collapsed 'pre'
    // state. rAF only fires when the tab actually paints, so a setTimeout fallback
    // guarantees we still reach 'open' if a paint is delayed — otherwise the
    // pop-out could stick in 'pre' and be uncloseable (see close()'s guard).
    let r2; const r1 = requestAnimationFrame(() => { r2 = requestAnimationFrame(() => setState((s) => (s === 'pre' ? 'open' : s))); });
    const fb = setTimeout(() => setState((s) => (s === 'pre' ? 'open' : s)), 120);
    return () => { cancelAnimationFrame(r1); if (r2) cancelAnimationFrame(r2); clearTimeout(fb); };
  }, [rect.height, rect.top]);

  /* autofocus the composer */
  useEffect(() => {
    const t = setTimeout(() => { if (taRef.current) taRef.current.focus({ preventScroll: true }); }, 180);
    return () => clearTimeout(t);
  }, []);

  const close = useCallback(() => {
    if (stRef.current === 'closing') return; // allow closing from 'pre' too, so a stalled open animation can't trap the pop-out
    setState('closing');
    const ms = Math.max(40, 380 * motionScale());
    setTimeout(() => {
      onClosed();
      if (returnFocus) requestAnimationFrame(() => requestAnimationFrame(() => { try { returnFocus.focus({ preventScroll: true }); } catch (e) { /* non-focusable */ } }));
    }, ms);
  }, [onClosed, returnFocus]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } };
    // The card is viewport-anchored, so a genuine viewport change (rotate, window
    // resize) invalidates its position → close. But a mobile soft keyboard resizes
    // only the HEIGHT (and fires 'resize'); closing on that would make the composer
    // vanish the instant a phone/tablet user taps it. So only close when the WIDTH
    // actually changes.
    const w0 = window.innerWidth;
    const onResize = () => { if (window.innerWidth !== w0) close(); };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('keydown', onKey, true); window.removeEventListener('resize', onResize); };
  }, [close]);

  /* Belt-and-suspenders focus trap: if focus ever lands outside the card while the
     pop-out is open (Tab race before autofocus, or the Add-Note button blurring to
     <body> as it disables during save), pull it back in. Inert while closing so
     returnFocus can do its job. */
  useEffect(() => {
    const onFocusIn = (ev) => {
      if (stRef.current !== 'open') return;
      const card = cardRef.current;
      if (card && !card.contains(ev.target)) card.focus({ preventScroll: true });
    };
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, []);

  const trapKey = (e) => {
    if (e.key !== 'Tab' || !cardRef.current) return;
    const list = Array.from(cardRef.current.querySelectorAll('button, textarea, a[href], [tabindex]:not([tabindex="-1"])')).filter((el) => !el.disabled && el.offsetParent !== null);
    if (!list.length) return;
    const first = list[0]; const last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  const save = async () => {
    const t = draft.trim();
    if (!t || busy) return;
    // Focus the composer before the button disables, so focus doesn't blur to
    // <body> during the network round-trip.
    if (taRef.current) taRef.current.focus({ preventScroll: true });
    setBusy(true);
    setErr(null);
    try {
      const added = await onAddNote(inv, t);
      if (added && added.id) setFreshId(added.id);
      setDraft('');
      if (threadRef.current) threadRef.current.scrollTop = 0;
      if (taRef.current) taRef.current.focus({ preventScroll: true });
    } catch (e) {
      // Keep the draft so nothing is lost; surface the failure inline.
      setErr((e && e.message) || 'Could not save note — please try again.');
    } finally { setBusy(false); }
  };
  const taKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); save(); } };

  const display = [...notes].reverse(); // newest first
  const replicaCtx = { mode: 'replica', notes, onOpenProject };

  return createPortal(
    <div className={'arn-layer' + (isDark ? ' theme-dark' : '') + (state === 'open' ? ' is-open' : '') + (state === 'closing' ? ' is-closing' : '')} style={{ '--arn-dy': dy + 'px' }}>
      <div className="arn-backdrop" onClick={close} />
      <div className="arn-card" ref={cardRef} role="dialog" aria-modal="true" tabIndex={-1}
        aria-label={'Collection notes — ' + inv.display_name + ' ' + inv.invoice_number}
        style={{ left: rect.left - 1, top: rect.top - 1, width: rect.width + 2, outline: 'none' }}
        onKeyDown={trapKey}>
        <div className="arn-tilt" style={{ transformOrigin: '50% 20%' }}>

          <div className="arn-rowstrip" style={{ height: rect.height }}>
            <div style={{ display: 'flex', alignItems: 'stretch', height: '100%', minWidth: 'max-content', marginLeft: -offX }}>
              {OUT_COLS.map((c, i) => (
                <div key={c.id} style={{ width: cells[i], flex: '0 0 auto', minWidth: 0, padding: CELL_PAD, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: c.align === 'right' ? 'flex-end' : c.align === 'center' ? 'center' : 'flex-start' }}>{c.cell(inv, replicaCtx)}</div>
              ))}
            </div>
          </div>

          <button className="arn-nx" onClick={close} aria-label="Close notes" style={{ right: 10, top: rect.height / 2 }}><Icon name="x" size={15} sw={2} /></button>

          <div className="arn-panel"><div>
            <div className="arn-panelin" ref={panelRef}>
              <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Collection Notes</span>
                  <span className="mono" style={{ minWidth: 18, height: 18, padding: '0 6px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'var(--text-2)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)' }}>{notes.length}</span>
                  <div style={{ flex: 1 }} />
                  <span style={{ fontSize: 11, color: 'var(--text-4)' }}>esc to close</span>
                </div>
                {notes.length ? (
                  <div className="arn-thread" ref={threadRef} style={{ overflowY: 'auto', maxHeight: 'min(300px, 38vh)', overscrollBehavior: 'contain', paddingRight: 6, display: 'flex', flexDirection: 'column' }}>
                    {display.map((n, i) => <NoteItem key={n.id} n={n} first={i === 0} last={i === display.length - 1} fresh={n.id === freshId} />)}
                  </div>
                ) : (
                  <div style={{ fontSize: 12.5, color: 'var(--text-4)', padding: '2px 0 2px 2px' }}>No notes yet — log the first touchpoint below.</div>
                )}
                <div>
                  <textarea className="arn-ta" ref={taRef} rows={2} value={draft} onChange={(e) => { setDraft(e.target.value); if (err) setErr(null); }} onKeyDown={taKey} placeholder={'Add a note for ' + inv.invoice_number + '…'} aria-label="Add a collection note" />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                    {err ? (
                      <span role="alert" style={{ fontSize: 12, fontWeight: 500, color: 'var(--danger)' }}>{err}</span>
                    ) : (
                      <span className="mono" style={{ fontSize: 11, color: 'var(--text-4)', letterSpacing: '-0.01em' }}>Cmd/Ctrl + Enter to save</span>
                    )}
                    <div style={{ flex: 1 }} />
                    <button className="arn-add" onClick={save} disabled={busy || !draft.trim()}>
                      <Icon name="plus" size={13} sw={2.2} /> {busy ? 'Saving…' : 'Add Note'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div></div>

        </div>
      </div>
    </div>,
    document.body
  );
};

const OutstandingTable = ({ rows, sortKey, sortDir, onSort, onOpenProject, notesMap, onOpenNotes, liftedKey }) => {
  const totalBalance = rows.reduce((s, r) => s + num(r.balance), 0);
  const plainTh = { position: 'sticky', top: 'var(--ar-th-top, 0px)', zIndex: 2, background: 'var(--bg-surface-2)', textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid var(--border-default)', fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)', whiteSpace: 'nowrap' };

  // Split into past due (days_past_due > 0) and not-yet-due, keeping the selected
  // sort within each group. Past due always sits on top with a divider between.
  const isPastDue = (r) => (r.days_past_due || 0) > 0;
  const pastDueRows = rows.filter(isPastDue);
  const notDueRows = rows.filter((r) => !isPastDue(r));
  const notDueTotal = notDueRows.reduce((s, r) => s + num(r.balance), 0);
  const showDivider = pastDueRows.length > 0 && notDueRows.length > 0;

  const renderRow = (r) => {
    const ctx = { mode: 'table', notes: notesMap[r.invoice_number] || [], onOpenProject };
    const rowKey = `${r.invoice_number}-${r.row_index}`;
    return (
      <tr
        key={rowKey}
        className="a-irow"
        tabIndex={0}
        aria-label={`Collection notes — ${r.display_name}, invoice ${r.invoice_number}, balance ${fmt$(num(r.balance))}`}
        onClick={(e) => onOpenNotes(r, e)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenNotes(r, e); } }}
        style={{ cursor: 'pointer', borderTop: '1px solid var(--border-subtle)', visibility: liftedKey === rowKey ? 'hidden' : undefined }}
      >
        {OUT_COLS.map((c) => (
          <td key={c.id} style={{ padding: CELL_PAD, textAlign: c.align || 'left', verticalAlign: 'middle' }}>{c.cell(r, ctx)}</td>
        ))}
      </tr>
    );
  };

  return (
    <div className="ar-card" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
      <div className="a-scroll">
        <table style={{ width: '100%', minWidth: 900, tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr>
              {OUT_COLS.map((c) => c.sortable === false
                ? <th key={c.id} style={{ ...plainTh, width: c.w, whiteSpace: 'normal' }}>{c.label}</th>
                : <SortTh key={c.id} id={c.id} label={c.label} align={c.align} w={c.w} sortKey={sortKey} sortDir={sortDir} onSort={onSort} />)}
            </tr>
          </thead>
          <tbody>
            {pastDueRows.map(renderRow)}
            {showDivider && (
              <tr key="__notdue_divider__">
                <td colSpan={OUT_COLS.length} style={{ padding: '10px 14px', background: 'var(--danger-soft)', borderTop: '2.5px solid var(--danger)', borderBottom: '2.5px solid var(--danger)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                    <span style={{ width: 8, height: 8, borderRadius: 999, flex: '0 0 auto', background: 'var(--danger)' }} />
                    <span style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--danger)' }}>Not yet due</span>
                    <span className="mono" style={{ minWidth: 18, height: 18, padding: '0 6px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'var(--success)', background: 'color-mix(in oklch, var(--success) 15%, transparent)', border: '1px solid color-mix(in oklch, var(--success) 40%, transparent)' }}>{notDueRows.length}</span>
                    <span className="mono" style={{ fontSize: 13, fontWeight: 700, color: 'var(--success)', fontVariantNumeric: 'tabular-nums' }}>{fmt$(notDueTotal)}</span>
                    <span style={{ fontSize: 11.5, color: 'color-mix(in oklch, var(--danger) 75%, var(--text-3))', fontWeight: 500 }}>— everything above is past due</span>
                  </div>
                </td>
              </tr>
            )}
            {notDueRows.map(renderRow)}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={7} style={{ position: 'sticky', bottom: 0, zIndex: 2, background: 'var(--bg-surface-2)', padding: '12px 14px', borderTop: '1px solid var(--border-default)', fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
                Totals
              </td>
              <FootMoney value={totalBalance} bold />
              <td style={{ position: 'sticky', bottom: 0, zIndex: 2, background: 'var(--bg-surface-2)', borderTop: '1px solid var(--border-default)' }} />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

/* ─────────────── Credited to zero ───────────────
   Invoices the credit memos numbered after them cancel in full (Duncan 2026-10-26, from accounting:
   "move it down to a new section below the current table that just shows the amount in the credit
   Memo that it equals out to zero"): out of the table, tiles and totals above, each with its credit. */

const CreditedTable = ({ rows, onOpenProject }) => {
  const invoiced = rows.reduce((s, r) => s + r.invoiced, 0);
  const credit = rows.reduce((s, r) => s + r.credit, 0);
  const net = rows.reduce((s, r) => s + r.net, 0);
  const th = { background: 'var(--bg-surface-2)', textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid var(--border-default)', fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)', whiteSpace: 'nowrap' };
  const numText = { fontSize: 14, fontWeight: 500, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
  const dateText = { display: 'block', marginTop: 2, fontSize: 12.5, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
  const foot = { background: 'var(--bg-surface-2)', padding: '12px 14px', borderTop: '1px solid var(--border-default)' };
  const footMoney = (value, color, bold) => (
    <td className="mono" style={{ ...foot, textAlign: 'right', fontSize: 14, fontWeight: bold ? 800 : 600, color: color || 'var(--text-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>{fmt$(value)}</td>
  );
  return (
    <section aria-label="Credited to zero" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '0 2px' }}>
        <h3 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: 'var(--text-1)' }}>Credited to zero</h3>
        <span className="mono" style={{ minWidth: 20, height: 20, padding: '0 7px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'var(--text-2)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-default)' }}>{rows.length}</span>
        <span style={{ fontSize: 13, color: 'var(--text-3)' }}>A credit memo with the same number cancels each one, so it isn’t counted above. Each leaves this list once its credit memo’s due date passes.</span>
      </div>
      <div className="ar-card" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
        <div className="a-scroll">
          <table style={{ width: '100%', minWidth: 720, tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr>
                <th style={{ ...th, width: '25%' }}>Project</th>
                <th style={{ ...th, width: '14%' }}>Invoice</th>
                <th style={{ ...th, width: '15%', textAlign: 'right' }}>Amount</th>
                <th style={{ ...th, width: '16%' }}>Credit Memo</th>
                <th style={{ ...th, width: '15%', textAlign: 'right' }}>Credit</th>
                <th style={{ ...th, width: '15%', textAlign: 'right' }}>Net</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.invoice_number}-${r.row_index}`} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: CELL_PAD }}><ProjectCellInner r={r} onOpenProject={onOpenProject} /></td>
                  <td style={{ padding: CELL_PAD }}>
                    <span className="mono" style={numText}>{r.invoice_number}</span>
                    <span className="mono" style={dateText}>{fmtDate(r.invoice_date)}</span>
                  </td>
                  <Money value={r.invoiced} />
                  <td style={{ padding: CELL_PAD }}>
                    {r.credits.map((c) => (
                      <div key={`${c.invoice_number}-${c.row_index}`}>
                        <span className="mono" style={numText}>{c.invoice_number}</span>
                        <span className="mono" style={dateText}>{fmtDate(c.invoice_date)}</span>
                      </div>
                    ))}
                  </td>
                  <Money value={r.credit} color="var(--success)" />
                  <Money value={r.net} bold />
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2} style={{ ...foot, fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Totals</td>
                {footMoney(invoiced)}
                <td style={foot} />
                {footMoney(credit, 'var(--success)')}
                {footMoney(net, null, true)}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </section>
  );
};

/* ─────────────── Cash received table ─────────────── */

const CashTable = ({ rows, sortKey, sortDir, onSort, onOpenProject, onAcct }) => {
  const total = rows.reduce((s, r) => s + r.collected, 0);
  return (
    <div className="ar-card" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
      <div className="a-scroll">
        <table style={{ width: '100%', minWidth: 980, borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr>
              <SortTh id="project" label="Project" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortTh id="invoice" label="Invoice" sortKey={sortKey} sortDir={sortDir} onSort={onSort} w={110} />
              <SortTh id="invdate" label="Invoice Date" sortKey={sortKey} sortDir={sortDir} onSort={onSort} w={126} />
              <SortTh id="paydate" label="Received" sortKey={sortKey} sortDir={sortDir} onSort={onSort} w={126} />
              <SortTh id="daystopay" label="Days to Pay" align="center" sortKey={sortKey} sortDir={sortDir} onSort={onSort} w={116} />
              <SortTh id="amount" label="Amount" align="right" sortKey={sortKey} sortDir={sortDir} onSort={onSort} w={150} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              return (
                <tr key={`${r.period}-${r.row_index}`} className="a-crow" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <ProjectCell row={r} onOpenProject={onOpenProject} />
                  <td className="mono" style={{ padding: '10px 14px', fontSize: 14, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{r.invoice_number || '—'}</td>
                  <td className="mono" style={{ padding: '10px 14px', fontSize: 13.5, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtDate(r.invoice_date)}</td>
                  <td className="mono" style={{ padding: '10px 14px', fontSize: 13.5, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtDate(r.payment_date)}</td>
                  <td className="mono" style={{ padding: '10px 14px', textAlign: 'center', fontSize: 13.5, fontWeight: 600, color: r.days_to_pay == null ? 'var(--text-4)' : agingTone(r.days_to_pay), fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{r.days_to_pay == null ? '—' : `${r.days_to_pay}d`}</td>
                  <Money value={r.collected} bold color={r.collected < 0 ? 'var(--danger)' : 'var(--success)'} />
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5} style={{ position: 'sticky', bottom: 0, zIndex: 2, background: 'var(--bg-surface-2)', padding: '12px 14px', borderTop: '1px solid var(--border-default)', fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
                Totals{onAcct.count > 0 && (
                  <span style={{ marginLeft: 10, fontWeight: 500, textTransform: 'none', letterSpacing: 0, fontSize: 12.5 }}>
                    + {onAcct.count} on-account legs netting <span className="mono" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt$(onAcct.net)}</span> (unapplied cash, no job)
                  </span>
                )}
              </td>
              <FootMoney value={total} bold color="var(--success)" />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

/* ═════════════════════════ Page ═════════════════════════ */

const AR_VIEWS = ['outstanding', 'cash', 'history', 'phase', 'statements'];
// The top bar's search box, by view (lib/pageSearch: it searches the view on screen).
const AR_SEARCH = {
  outstanding: 'Search outstanding…',
  cash: 'Search cash received…',
  history: 'Search invoices…',
  phase: 'Search billing by phase…',
  statements: 'Search statements…',
};

const ARPage = ({ onOpenProject }) => {
  const [aging, setAging] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [projects, setProjects] = useState([]);
  const [monthCache, setMonthCache] = useState({});
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  // The view is in the URL (/ar?view=history), so a link or a reload lands on it. Invoice History
  // lives here since 2026-10-15 (Duncan: "how close to the AR tab this is, can we combine the 2
  // tabs"); /invoice-history redirects to it.
  const [searchParams, setSearchParams] = useSearchParams();
  const view = AR_VIEWS.includes(searchParams.get('view')) ? searchParams.get('view') : 'outstanding';
  const setView = useCallback((v) => setSearchParams(v === 'outstanding' ? {} : { view: v }, { replace: true }), [setSearchParams]);
  const [loading, setLoading] = useState(true);
  const [monthLoading, setMonthLoading] = useState(false);
  const [error, setError] = useState(null);

  const [projectFilter, setProjectFilter] = useState('all');
  const [pmFilter, setPmFilter] = useState('all');
  const [cmFilter, setCmFilter] = useState('all');
  const [notesMap, setNotesMap] = useState({});
  const [lift, setLift] = useState(null); // { inv, rect, cells, offX, returnFocus } — the 3D notes pop-out
  const rootRef = useRef(null);
  const filterRef = useRef(null);
  const [outSort, setOutSort] = useState({ key: 'age', dir: 'desc' }); // most overdue first, like the Open AR workbook
  const [cashSort, setCashSort] = useState({ key: 'amount', dir: 'desc' });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [agingRows, periodRows, projectRows, noteRows] = await Promise.all([
        getArSyncAging(),
        getArSyncCollectionPeriods(),
        getProjects().catch(() => []),
        getArNotes().catch(() => []),
      ]);
      setAging(Array.isArray(agingRows) ? agingRows : []);
      const p = Array.isArray(periodRows) ? periodRows : [];
      setPeriods(p);
      setProjects(Array.isArray(projectRows) ? projectRows : []);
      const nm = {};
      (Array.isArray(noteRows) ? noteRows : []).forEach((n) => {
        (nm[n.invoice_number] = nm[n.invoice_number] || []).push(n);
      });
      setNotesMap(nm);
      if (p.length > 0) setSelectedPeriod((cur) => cur || p[0].period);
    } catch (err) {
      console.error('AR load failed:', err);
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Lazy-load the selected month's payment rows (cached per period).
  useEffect(() => {
    if (!selectedPeriod || monthCache[selectedPeriod]) return;
    let stale = false;
    setMonthLoading(true);
    getArSyncCollections(selectedPeriod)
      .then((rows) => { if (!stale) setMonthCache((prev) => ({ ...prev, [selectedPeriod]: Array.isArray(rows) ? rows : [] })); })
      .catch((err) => console.error('Collections month load failed:', err))
      .finally(() => { if (!stale) setMonthLoading(false); });
    return () => { stale = true; };
  }, [selectedPeriod, monthCache]);

  // CE job number → Summit project (same linkage the Job Costs page uses).
  const projectLookup = useMemo(() => {
    const map = {};
    projects.forEach((p) => {
      if (p.project_number) map[p.project_number] = p;
      if (p.ce_job_id) map[p.ce_job_id] = p;
    });
    return map;
  }, [projects]);

  const decorate = useCallback((r) => {
    const proj = r.job_id ? projectLookup[r.job_id] : null;
    return {
      ...r,
      display_name: (proj && proj.project_name) || r.job_name || (r.job_id ? r.job_id : 'No job'),
      display_number: (proj && proj.project_number) || r.job_id || '',
      client_name: (proj && proj.client_name) || '',
      project_ref: proj ? (proj.slug || proj.id) : null,
      // Canonical PM/CM from the linked Summit project (same source Job Costs
      // filters on) — clean names, unlike the CE report's combined free-text.
      pm: (proj && proj.project_manager) || '',
      cm: (proj && proj.construction_manager) || '',
    };
  }, [projectLookup]);

  // PM can hold several comma-separated names; matches if any equals the pick.
  const nameList = (s) => String(s || '').split(',').map((x) => x.trim()).filter(Boolean);

  /* ── Outstanding — matches the accounting team's Open AR workbook: job
     invoices with a POSITIVE balance only. ON ACCT rows, CR/DM memos, and
     credit balances are CE bookkeeping (unapplied cash), not collectible
     receivables — the mirror keeps them, this view doesn't show them. Each
     invoice is net of the credit memos numbered after it (lib/arTotals
     outstandingOf): one they cancel in full moves to "Credited to zero" below
     the table, out of the tiles and totals. ── */
  const outstanding = useMemo(() => outstandingOf(aging), [aging]);
  const openRows = useMemo(() => outstanding.open.map(decorate), [outstanding, decorate]);
  // A credited pair leaves "Credited to zero" once its credit memo's due date has passed (Duncan 2026-10-26: "They
  // should expire off of that credited to zero list when the due date is passed" -- the credit memo's, his pick); it
  // stays out of Open AR all the same.
  const creditedRows = useMemo(() => {
    const today = todayCentral();
    return outstanding.credited.filter((r) => {
      const due = r.credits.map((c) => String(c.due_date || '').slice(0, 10)).filter(Boolean).sort().pop();
      return !due || due >= today;
    }).map(decorate);
  }, [outstanding, decorate]);

  // Cash rows for the selected month (defined here so the PM/CM option lists can
  // union both tabs' people; sign flip: CE reports collections negative).
  const monthRows = monthCache[selectedPeriod] || [];
  const cashRows = useMemo(() => monthRows
    .filter((r) => r.job_id)
    .map((r) => ({ ...decorate(r), collected: -num(r.amount), days_to_pay: daysBetween(r.invoice_date, r.payment_date) })), [monthRows, decorate]);

  // PM + Manager (CM) options — from the linked projects across BOTH views, so
  // the shared filter works whichever tab you're on (matches Job Costs).
  const pmOptions = useMemo(() => {
    const set = new Set();
    [...openRows, ...creditedRows, ...cashRows].forEach((r) => nameList(r.pm).forEach((n) => set.add(n)));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [openRows, creditedRows, cashRows]);
  const cmOptions = useMemo(() => {
    const set = new Set();
    [...openRows, ...creditedRows, ...cashRows].forEach((r) => nameList(r.cm).forEach((n) => set.add(n)));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [openRows, creditedRows, cashRows]);

  const projectOptions = useMemo(() => {
    const seen = new Map();
    [...openRows, ...creditedRows, ...cashRows].forEach((r) => {
      if (r.job_id && !seen.has(r.job_id)) seen.set(r.job_id, r.display_name);
    });
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [openRows, creditedRows, cashRows]);

  const peopleFiltered = pmFilter !== 'all' || cmFilter !== 'all';
  const matchesPeople = useCallback((r) => (
    (pmFilter === 'all' || nameList(r.pm).includes(pmFilter)) &&
    (cmFilter === 'all' || nameList(r.cm).includes(cmFilter))
  ), [pmFilter, cmFilter]);

  /* The top bar's search (lib/pageSearch) is this page's for every view: its placeholder names the
     view, and the text empties when you switch views (the views change without a route change).
     Outstanding and Cash Received filter by it with the Project / PM / CM filters, so the tiles,
     the aging bar and the totals follow it; the other views get the text (Statements shares its box). */
  const [search, setSearch] = usePageSearchState(AR_SEARCH[view]);
  useEffect(() => { setSearch(''); }, [view, setSearch]);
  const searching = search.trim() !== '';
  const matchesText = useCallback((r) => matchesSearch(search, r.display_name, r.display_number, r.job_id, r.job_name,
    r.client_name, r.invoice_number, r.business_development, r.project_manager, r.pm, r.cm,
    (r.credits || []).map((c) => c.invoice_number)), [search]);

  const matchesOut = useCallback((r) => (projectFilter === 'all' || r.job_id === projectFilter) && matchesPeople(r) && matchesText(r),
    [projectFilter, matchesPeople, matchesText]);
  const filteredOpen = useMemo(() => openRows.filter(matchesOut), [openRows, matchesOut]);
  const filteredCredited = useMemo(() => creditedRows.filter(matchesOut), [creditedRows, matchesOut]);

  const outTotals = useMemo(() => arOutTotals(filteredOpen), [filteredOpen]);

  // 3 tiers, matching the accounting workbook's legend (<30 / 30–60 / >60).
  const agingBuckets = useMemo(() => arAgingBuckets(filteredOpen).map((b) => ({ ...b, tone: agingTone(b.toneAt) })), [filteredOpen]);

  const sortedOpen = useMemo(
    () => sortRows(filteredOpen, OUT_SORT, outSort.key, outSort.dir),
    [filteredOpen, outSort]
  );

  /* ── Cash received (selected month) — same Project / PM / CM filters as Outstanding ── */
  const filteredCash = useMemo(() => cashRows.filter((r) =>
    (projectFilter === 'all' || r.job_id === projectFilter) && matchesPeople(r) && matchesText(r)
  ), [cashRows, projectFilter, matchesPeople, matchesText]);
  const onAcct = useMemo(() => {
    // Only meaningful for the whole month (on-account legs carry no job/PM).
    if (peopleFiltered) return { count: 0, net: 0 };
    const legs = monthRows.filter((r) => !r.job_id);
    return { count: legs.length, net: legs.reduce((s, r) => s - num(r.amount), 0) };
  }, [monthRows, peopleFiltered]);
  const sortedCash = useMemo(
    () => sortRows(filteredCash, CASH_SORT, cashSort.key, cashSort.dir),
    [filteredCash, cashSort]
  );

  const cashTotals = useMemo(() => {
    const year = selectedPeriod ? selectedPeriod.slice(0, 4) : '';
    // Month tile is filter-aware (sum the filtered rows). YTD stays company-wide
    // (spans months not loaded client-side); shown as a reference.
    const monthCollected = filteredCash.reduce((s, r) => s + r.collected, 0);
    const ytd = periods.filter((p) => p.period.startsWith(year)).reduce((s, p) => s + (-num(p.job_collected)), 0);
    return { monthCollected, ytd, year, avgDaysToPay: avgDaysToPay(filteredCash) };
  }, [periods, selectedPeriod, filteredCash]);

  const snapshotDate = aging && aging.length > 0 ? aging[0].report_date : null;
  const outFiltered = projectFilter !== 'all' || peopleFiltered;

  const onOutSort = (key) => setOutSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));
  const onCashSort = (key) => setCashSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  /* Row click → measure the <tr> and portal a pixel-aligned replica that lifts.
     The measurement MUST run synchronously here — React resets e.currentTarget to
     null once this handler returns, so it can't be read from a deferred setState
     updater. Compute the rect now; use the functional setLift only to guard against
     opening a second pop-out while one is live. */
  const openLift = useCallback((row, e) => {
    const tr = e && e.currentTarget;
    if (!tr || !tr.getBoundingClientRect) return;
    const b = tr.getBoundingClientRect();
    const cells = Array.from(tr.children).map((td) => td.getBoundingClientRect().width);
    const sc = tr.closest('.a-scroll');
    const sr = sc ? sc.getBoundingClientRect() : null;
    const left = Math.max(b.left, sr ? sr.left : -1e9, 8);
    const right = Math.min(b.right, sr ? sr.right : 1e9, window.innerWidth - 8);
    if (right - left < 120) return;
    const next = { inv: row, rect: { left, top: b.top, width: right - left, height: b.height }, cells, offX: left - b.left, returnFocus: tr };
    setLift((cur) => cur || next);
  }, []);

  /* Lock page scroll while the pop-out is open (its rect is viewport-anchored). */
  useEffect(() => {
    if (!lift) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [lift]);

  const handleAddNote = useCallback(async (invoiceRow, text) => {
    const created = await addArNote({ invoice_number: invoiceRow.invoice_number, job_id: invoiceRow.job_id || null, note: text });
    setNotesMap((prev) => ({ ...prev, [invoiceRow.invoice_number]: [...(prev[invoiceRow.invoice_number] || []), created] }));
    return created;
  }, []);

  // The column headers stick under the frozen filter bar, whose height depends on how it wraps.
  useLayoutEffect(() => {
    const root = rootRef.current;
    const bar = filterRef.current;
    if (!root || !bar) return undefined;
    const set = () => root.style.setProperty('--ar-filter-h', `${bar.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(bar);
    return () => ro.disconnect();
  }, [view, loading, error]);

  // Invoice History and Statements load their own data, so they don't wait for the AR tables.
  const ownData = view === 'history' || view === 'phase' || view === 'statements';
  if (loading && !ownData) return <LoadingState message="Loading accounts receivable…" />;

  const viewBar = (
    <section style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', padding: '10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
      <SegmentedControl
        value={view}
        onChange={setView}
        options={[
          { value: 'outstanding', label: 'Outstanding', count: loading ? undefined : outTotals.invoices },
          { value: 'cash', label: 'Cash Received', count: loading ? undefined : cashRows.length },
          { value: 'history', label: 'Invoice History' },
          { value: 'phase', label: 'Billing by Phase' },
          { value: 'statements', label: 'Statements' },
        ]}
      />
      <div style={{ flex: 1, minWidth: 8 }} />
      {view === 'cash' ? (
        <>
          <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-3)' }}>Month</span>
          <Select value={selectedPeriod || ''} onChange={setSelectedPeriod} minWidth={180}>
            {periods.map((p) => (
              <option key={p.period} value={p.period}>{fmtPeriod(p.period)}</option>
            ))}
          </Select>
        </>
      ) : snapshotDate ? (
        <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          From ComputerEase · as of {fmtDate(snapshotDate)}
        </span>
      ) : null}
    </section>
  );

  return (
    <div className="ar-page" ref={rootRef} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`
        /* Freeze the filter bar and the column headers while the page scrolls (Duncan 2026-10-15:
           "that same freeze top rows", as on BoMoCo); the figures and the aging bar scroll away.
           They stick to the window under the 56px app header (AppHeader.js), so this page's
           .app-main mustn't be a scroll container -- it never scrolls (the window does), as on
           the project pages. Only where the tables fit across: below that they scroll sideways
           in their own box, which a header can't stick out of. */
        .app-main:has(.ar-page) { overflow-y: visible; overflow-x: hidden; overflow-x: clip; }
        .ar-card { overflow: hidden; overflow: clip; }
        /* The old App.css clears every table cell's background in dark mode; the totals row now sticks
           to the bottom of the window, so it needs its own or the rows show through it. */
        .app.dark .app-main .ar-card tfoot td { background: var(--bg-surface-2) !important; }
        .a-scroll { overflow-x: auto; }
        @media (min-width: 1290px) {
          .a-scroll { overflow-x: clip; }
          .ar-sticky { position: sticky; top: 56px; z-index: 6; }
          .ar-page { --ar-th-top: calc(56px + var(--ar-filter-h, 0px)); }
        }
        /* Header labels on one line -- but ONLY where they actually fit.
           The Outstanding table is tableLayout:fixed with PERCENTAGE columns, so its
           cells shrink with the viewport and an unconditional nowrap trades a tall
           header for a silently CLIPPED one, which is the worse failure. Measured by
           shrinking the real table: with the shortened labels (Biz Dev / PM / Past Due)
           nowrap is clean down to a 1050px table -- about a 1352px viewport -- and
           starts clipping ~1330 ("INVOICE" by 1px first). Gate at 1340 and let the
           labels wrap below it, exactly as they do today: taller, but never truncated.
           Worth 68px -> 36px on Outstanding and 52px -> 36px on Cash Received. */
        @media (min-width: 1340px) {
          .a-th { white-space: nowrap; }
        }
        /* Quiet-ledger KPI row (matches Job Costs / Workload). */
        @media (max-width: 960px) {
          .akr { display: grid !important; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; padding: 0 !important; background: transparent !important; border: none !important; box-shadow: none !important; }
          .akr .akr-div { display: none; }
          .akr .akr-tile { min-width: 0 !important; background: var(--bg-surface); border: 1px solid var(--border-default); border-radius: 10px; box-shadow: var(--shadow-xs); }
        }
        .a-clear:hover { border-color: color-mix(in oklch, var(--accent) 35%, var(--border-default)); color: var(--accent-text); background: var(--accent-soft); }
        .a-th:hover { color: var(--accent-text); }
        .a-th:hover .a-sorticon { opacity: 1; }
        .a-irow:hover { background: var(--bg-hover); }
        .a-irow:hover .a-pname { color: var(--accent-text); }
        .a-irow:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; background: var(--bg-hover); }
        .a-select:focus-visible { outline: none; border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-ring); }
        .a-projlink:hover, .a-projlink:hover span { color: var(--accent-text) !important; }
        .a-note-cell:hover { background: var(--accent-soft); }
        .a-note-ta:focus { border-color: var(--accent) !important; box-shadow: 0 0 0 3px var(--accent-ring) !important; }
        .a-modal-title:hover:not(:disabled) { color: var(--accent-text) !important; }
        @keyframes arFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes arRise { from { opacity: 0; transform: translateY(8px) scale(0.99); } to { opacity: 1; transform: none; } }
      `}</style>

      {view === 'history' ? (
        <>
          {viewBar}
          <InvoiceHistoryPage onOpenProject={onOpenProject} search={search} />
        </>
      ) : view === 'phase' ? (
        <>
          {viewBar}
          <BillingByPhaseView onOpenProject={onOpenProject} search={search} />
        </>
      ) : view === 'statements' ? (
        <>
          {viewBar}
          <StatementsView searchState={[search, setSearch]} />
        </>
      ) : error ? (
        <StateScreen kind="server-error" title="Couldn’t load accounts receivable" body="Something went wrong reading the CE receivables mirror. Check your connection and try again." primary={{ label: 'Retry', onClick: loadData }} />
      ) : (aging || []).length === 0 ? (
        <StateScreen kind="empty" title="No CE receivables data yet" body="Once the CE-Agent runs an AR pull, open invoices and received payments will appear here." />
      ) : (
        <>
          {viewBar}

          {/* Six-stat summary + aging — identical on both tabs (all filter-aware). */}
          <KpiRow tiles={[
            { label: 'Total Open AR', value: fmt$(outTotals.net), valueSize: 20, sub: `${outTotals.invoices} open ${outTotals.invoices === 1 ? 'invoice' : 'invoices'}`, muted: !outTotals.net },
            { label: 'Total Past Due', value: fmt$(outTotals.overdue), valueSize: 20, valueColor: outTotals.overdue > 0 ? 'var(--warning)' : undefined, sub: `${outTotals.overdueCount} ${outTotals.overdueCount === 1 ? 'invoice' : 'invoices'} past due`, muted: !outTotals.overdue },
            { label: 'Percent of Total', value: `${outTotals.pctPastDue.toFixed(1)}%`, valueSize: 20, valueColor: outTotals.pctPastDue > 0 ? agingTone(outTotals.pctPastDue > 25 ? 90 : outTotals.pctPastDue > 10 ? 45 : 0) : undefined, sub: 'of open AR is past due', muted: !outTotals.overdue },
            { label: `Collected — ${fmtPeriod(selectedPeriod)}`, value: fmt$(cashTotals.monthCollected), valueSize: 20, valueColor: cashTotals.monthCollected > 0 ? 'var(--success)' : undefined, sub: `${filteredCash.length} ${filteredCash.length === 1 ? 'payment' : 'payments'} on jobs`, muted: !cashTotals.monthCollected },
            { label: `Collected — ${cashTotals.year} YTD`, value: fmt$(cashTotals.ytd), valueSize: 20, sub: peopleFiltered ? 'company-wide (all PMs)' : 'job-applied cash', muted: !cashTotals.ytd },
            { label: 'Avg Days to Pay', value: cashTotals.avgDaysToPay == null ? '—' : `${cashTotals.avgDaysToPay}d`, valueSize: 20, valueColor: cashTotals.avgDaysToPay == null ? undefined : agingTone(cashTotals.avgDaysToPay), sub: 'invoice → payment', muted: cashTotals.avgDaysToPay == null },
          ]} />

          <AgingCard buckets={agingBuckets} />

          {view === 'outstanding' ? (
            <>
              <section className="ar-sticky" ref={filterRef} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
                <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-3)', paddingLeft: 2 }}>Project</span>
                <Select value={projectFilter} onChange={setProjectFilter} minWidth={236}>
                  <option value="all">All projects</option>
                  {projectOptions.map(([jobId, name]) => (
                    <option key={jobId} value={jobId}>{name}</option>
                  ))}
                </Select>
                <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-3)', paddingLeft: 6 }}>PM</span>
                <Select value={pmFilter} onChange={setPmFilter} minWidth={170}>
                  <option value="all">All PMs</option>
                  {pmOptions.map((pm) => (
                    <option key={pm} value={pm}>{pm}</option>
                  ))}
                </Select>
                <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-3)', paddingLeft: 6 }}>CM</span>
                <Select value={cmFilter} onChange={setCmFilter} minWidth={170}>
                  <option value="all">All CMs</option>
                  {cmOptions.map((cm) => (
                    <option key={cm} value={cm}>{cm}</option>
                  ))}
                </Select>
                {outFiltered && (
                  <button type="button" className="a-clear" onClick={() => { setProjectFilter('all'); setPmFilter('all'); setCmFilter('all'); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px', borderRadius: 8, fontSize: 14, fontWeight: 500, color: 'var(--text-2)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer' }}>
                    <Icon name="x" size={13} sw={2} /> Clear filters
                  </button>
                )}
                <div style={{ flex: 1, minWidth: 8 }} />
                <div className="mono" style={{ fontSize: 14, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
                  Showing <span style={{ color: 'var(--text-1)', fontWeight: 600 }}>{sortedOpen.length}</span> of {openRows.length} entries
                  {(outFiltered || searching) && (
                    <>
                      <span style={{ color: 'var(--text-4)' }}>{'  ·  '}</span>
                      Filtered total: <span style={{ color: 'var(--accent-text)', fontWeight: 700 }}>{fmt$(outTotals.net)}</span>
                    </>
                  )}
                </div>
              </section>

              {sortedOpen.length === 0 && searching && openRows.length > 0 ? (
                <StateScreen kind="empty" {...noMatchText(search)} />
              ) : sortedOpen.length === 0 ? (
                <StateScreen kind="empty" title="No open invoices match these filters" body="Try a different project or PM, or clear the filters." />
              ) : (
                <OutstandingTable rows={sortedOpen} sortKey={outSort.key} sortDir={outSort.dir} onSort={onOutSort} onOpenProject={onOpenProject} notesMap={notesMap} onOpenNotes={openLift} liftedKey={lift ? `${lift.inv.invoice_number}-${lift.inv.row_index}` : null} />
              )}

              {filteredCredited.length > 0 && <CreditedTable rows={filteredCredited} onOpenProject={onOpenProject} />}
            </>
          ) : (
            <>
              <section className="ar-sticky" ref={filterRef} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
                <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-3)', paddingLeft: 2 }}>Project</span>
                <Select value={projectFilter} onChange={setProjectFilter} minWidth={236}>
                  <option value="all">All projects</option>
                  {projectOptions.map(([jobId, name]) => (
                    <option key={jobId} value={jobId}>{name}</option>
                  ))}
                </Select>
                <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-3)', paddingLeft: 6 }}>PM</span>
                <Select value={pmFilter} onChange={setPmFilter} minWidth={170}>
                  <option value="all">All PMs</option>
                  {pmOptions.map((pm) => (
                    <option key={pm} value={pm}>{pm}</option>
                  ))}
                </Select>
                <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-3)', paddingLeft: 6 }}>CM</span>
                <Select value={cmFilter} onChange={setCmFilter} minWidth={170}>
                  <option value="all">All CMs</option>
                  {cmOptions.map((cm) => (
                    <option key={cm} value={cm}>{cm}</option>
                  ))}
                </Select>
                {outFiltered && (
                  <button type="button" className="a-clear" onClick={() => { setProjectFilter('all'); setPmFilter('all'); setCmFilter('all'); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px', borderRadius: 8, fontSize: 14, fontWeight: 500, color: 'var(--text-2)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer' }}>
                    <Icon name="x" size={13} sw={2} /> Clear filters
                  </button>
                )}
                <div style={{ flex: 1, minWidth: 8 }} />
                <div className="mono" style={{ fontSize: 14, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
                  Showing <span style={{ color: 'var(--text-1)', fontWeight: 600 }}>{sortedCash.length}</span> of {cashRows.length} payments
                </div>
              </section>

              {monthLoading ? (
                <LoadingState message="Loading payments…" />
              ) : sortedCash.length === 0 && searching && cashRows.length > 0 ? (
                <StateScreen kind="empty" {...noMatchText(search)} />
              ) : sortedCash.length === 0 ? (
                <StateScreen kind="empty" title="No job payments match" body={outFiltered || searching ? 'No payments for these filters in this month — try another month or clear the filters.' : 'CE recorded no job-applied cash for this period.'} />
              ) : (
                <CashTable rows={sortedCash} sortKey={cashSort.key} sortDir={cashSort.dir} onSort={onCashSort} onOpenProject={onOpenProject} onAcct={onAcct} />
              )}
            </>
          )}
        </>
      )}

      {lift && (
        <NotesLift
          inv={lift.inv}
          notes={notesMap[lift.inv.invoice_number] || []}
          rect={lift.rect}
          cells={lift.cells}
          offX={lift.offX}
          returnFocus={lift.returnFocus}
          onAddNote={handleAddNote}
          onOpenProject={onOpenProject}
          onClosed={() => setLift(null)}
        />
      )}
    </div>
  );
};

export default ARPage;
